import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser, useDivisionId, useDivisionRole } from '../../../features/auth/hooks/useUser';
import { useUserDivisions } from '../../../features/organization/hooks/useUserDivisions';
import { useTheme } from '../../../styles/ThemeContext';
import { switchDivisionSession } from '../../../features/organization/utils/switchDivisionSession';
import DivisionSwitchLoader from '../../../features/organization/components/DivisionSwitchLoader';

// Matches the DIVISION_ROLE_LABELS convention used across the Organization pages
// (DivisionDetail.jsx, MembersPage.jsx) — DIVISION_ADMIN gets its own label, everything else is Member.
const getDivisionRoleLabel = (role) => role === 'DIVISION_ADMIN' ? 'Division Admin' : 'Member';

// Full skeleton pill shown while useUserDivisions() is still fetching — matches the approved
// wireframe (.claude/wireframes/division-switcher-loading-wireframes.html, Option 5). Icon,
// caption, and name are all shimmering placeholders, and the pill drops its border/shadow so
// it doesn't read as a clickable control yet.
const DivisionPillSkeleton = ({ isDarkMode }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '7px 16px 7px 8px',
    borderRadius: '999px',
    background: 'var(--surface-color, #f8f9fa)',
    border: '1.5px solid var(--border-color, rgba(0,0,0,0.08))',
    flexShrink: 0,
    fontFamily: 'Poppins, sans-serif'
  }}>
    <div className="division-pill-skeleton-shimmer" style={{ width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0 }} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <div className="division-pill-skeleton-shimmer" style={{ width: '40px', height: '8px', borderRadius: '3px' }} />
      <div className="division-pill-skeleton-shimmer" style={{ width: '70px', height: '12px', borderRadius: '4px' }} />
    </div>

    <style>{`
      .division-pill-skeleton-shimmer {
        background: linear-gradient(90deg,
          color-mix(in srgb, var(--text-muted, #999) ${isDarkMode ? '28%' : '18%'}, transparent) 25%,
          color-mix(in srgb, var(--text-muted, #999) ${isDarkMode ? '42%' : '32%'}, transparent) 50%,
          color-mix(in srgb, var(--text-muted, #999) ${isDarkMode ? '28%' : '18%'}, transparent) 75%);
        background-size: 200% 100%;
        animation: division-pill-skeleton-shimmer-move 1.3s ease-in-out infinite;
      }
      @keyframes division-pill-skeleton-shimmer-move {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  </div>
);

const DivisionSwitcher = () => {
  const { setUserData } = useUser();
  const currentDivisionId = useDivisionId();
  const currentDivisionRole = useDivisionRole();
  const { theme, isDarkMode } = useTheme();
  const themeGradient = theme.gradients?.primary || theme.primary;
  const { divisions, isLoading: isLoadingDivisions } = useUserDivisions();

  const [isOpen, setIsOpen] = useState(false);
  const [switchingDivisionId, setSwitchingDivisionId] = useState(null);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSwitch = useCallback(async (divId) => {
    if (divId === currentDivisionId || switchingDivisionId) return;

    setSwitchingDivisionId(divId);
    setError('');

    try {
      await switchDivisionSession(divId, setUserData);
      // On success, switchDivisionSession navigates away — nothing left to do here.
    } catch (err) {
      setError(err.message);
      setSwitchingDivisionId(null);
    }
  }, [currentDivisionId, switchingDivisionId, setUserData]);

  const currentDivision = divisions.find(d => d.divisionId === currentDivisionId);
  const canSwitch = divisions.length > 1;

  // A user not actually added to any division (e.g. a tenant admin fresh off
  // onboarding) still gets a default divisionId from the backend, but no
  // divisionRole — divisionId alone is not proof of membership, so gate on
  // role instead. Without this, such users saw a bare "Division" pill with
  // no real division backing it.
  if (!currentDivisionRole) {
    return null;
  }

  // Divisions haven't loaded yet — currentDivision is still undefined, so the label would
  // otherwise fall back to the role/"Division" for a couple of seconds on every login before
  // popping to the real name. Show a skeleton pill instead of that flash of wrong text.
  if (isLoadingDivisions && !currentDivision) {
    return <DivisionPillSkeleton isDarkMode={isDarkMode} />;
  }

  const divisionLabel = currentDivision?.divisionName || currentDivisionRole || 'Division';

  // Icon-first button: gradient icon circle + "DIVISION" caption above the name +
  // a subtle gradient border, so it reads as its own first-class control (same
  // visual weight as the bell/avatar) rather than a passive status readout.
  const buttonStyle = {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '7px 16px 7px 8px',
    borderRadius: '999px',
    border: '1.5px solid transparent',
    background: isDarkMode
      ? `linear-gradient(var(--surface-color, #252526), var(--surface-color, #252526)) padding-box, ${themeGradient} border-box`
      : `linear-gradient(#EEE, #EEE) padding-box, ${themeGradient} border-box`,
    boxShadow: isDarkMode
      ? '-2.4px -2.4px 6px 0 rgba(60,60,60,0.3), 3.6px 3.6px 6px 0 rgba(0,0,0,0.5)'
      : '-2.396px -2.396px 5.989px 0 rgba(255,255,255,0.55), 3.594px 3.594px 5.989px 0 rgba(136,160,183,0.35)',
    flexShrink: 0,
    fontFamily: 'Poppins, sans-serif',
    color: 'var(--text-primary, #1a1a1a)'
  };

  const iconCircleStyle = {
    width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
    background: themeGradient,
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'
  };

  // Fused onto the pill's right edge — the division role travels with the division it
  // belongs to, so pairing them in one control keeps "this is division-scoped" unambiguous
  // and distinct from the tenant-level role pill at the bottom of the sidebar.
  const roleChipStyle = {
    fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    padding: '3px 9px', borderRadius: '999px', whiteSpace: 'nowrap', flexShrink: 0,
    background: 'color-mix(in srgb, var(--primary-color, #9404C3) 14%, transparent)',
    color: 'var(--primary-color, #9404C3)'
  };

  const DivisionButtonContent = () => (
    <>
      <div style={iconCircleStyle}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
          <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
          <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
        </svg>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
        <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted, #999)' }}>
          Division
        </span>
        <span style={{ fontSize: '13px', fontWeight: 700 }}>{divisionLabel}</span>
      </div>
      <div style={{ width: '1px', height: '20px', background: 'var(--border-color, rgba(0,0,0,0.1))', flexShrink: 0 }} />
      <span style={roleChipStyle}>{getDivisionRoleLabel(currentDivisionRole)}</span>
    </>
  );

  if (!canSwitch) {
    return (
      <div style={{ ...buttonStyle, cursor: 'default' }} title="Current Division">
        <DivisionButtonContent />
      </div>
    );
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{ ...buttonStyle, cursor: 'pointer' }}
      >
        <DivisionButtonContent />
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #9404C3)" strokeWidth="2.5"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0,
          minWidth: '220px', background: 'var(--card-background, #fff)',
          borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
          overflow: 'hidden', zIndex: 100000
        }}>
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-color, rgba(0,0,0,0.06))',
            background: 'var(--surface-color, #f8f9fa)',
            fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary, #6b7280)'
          }}>
            Switch Division
          </div>

          {error && (
            <div style={{ padding: '10px 16px', fontSize: '12px', color: '#ef4444' }}>
              {error}
            </div>
          )}

          <div style={{ padding: '8px' }}>
            {divisions.map((division) => {
              const isCurrent = division.divisionId === currentDivisionId;
              const isSwitching = switchingDivisionId === division.divisionId;
              return (
                <button
                  key={division.divisionId}
                  onClick={() => handleSwitch(division.divisionId)}
                  disabled={isCurrent || !!switchingDivisionId}
                  style={{
                    width: '100%', padding: '10px 14px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: isCurrent ? 'var(--surface-color, #f8f9fa)' : 'none',
                    border: 'none', borderRadius: '10px',
                    cursor: isCurrent || switchingDivisionId ? 'default' : 'pointer',
                    fontSize: '14px', fontWeight: 500, textAlign: 'left',
                    color: 'var(--text-primary, #1a1a1a)',
                    opacity: switchingDivisionId && !isSwitching ? 0.5 : 1
                  }}
                >
                  <span>{division.divisionName || division.divisionId}</span>
                  {isSwitching ? (
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary, #6b7280)' }}>Switching…</span>
                  ) : isCurrent ? (
                    <span style={{ fontSize: '11px', color: 'var(--primary-color, #9404C3)' }}>Current</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {switchingDivisionId && (
        <DivisionSwitchLoader
          divisionName={divisions.find(d => d.divisionId === switchingDivisionId)?.divisionName}
        />
      )}
    </div>
  );
};

export default DivisionSwitcher;
